import axios from "axios";
import {
  slugifyWorkspaceName,
  tenantDomainLabel,
  tenantSlugFromHost,
  workspacePreviewUrl,
} from "@/utils/hostname";

/** Always hit the configured central API (never the current tenant host). */
export function centralApiBaseUrl() {
  const configured = process.env.VUE_APP_API_URL || "http://127.0.0.1:8000/api/";
  return configured.endsWith("/") ? configured : `${configured}/`;
}

/**
 * Look up whether a workspace slug exists.
 * @returns {Promise<{
 *   slug: string,
 *   exists: boolean,
 *   reserved: boolean,
 *   domain: string|null,
 *   redirectUrl: string|null,
 *   message: string|null,
 * }>}
 */
export async function lookupWorkspace(rawSlug, { signal } = {}) {
  const slug = slugifyWorkspaceName(rawSlug);
  if (!slug) {
    return {
      slug: "",
      exists: false,
      reserved: false,
      domain: null,
      redirectUrl: null,
      message: "Enter your workspace name.",
    };
  }

  const res = await axios.get(`${centralApiBaseUrl()}central/tenant-lookup`, {
    params: { slug },
    signal,
  });

  const data = res.data || {};
  const message = String(data.message || "");
  const reserved = message.toLowerCase().includes("reserved");
  const available = data.available === true;
  const exists = !available && !reserved && Boolean(data.tenant_id || data.redirect_url);

  return {
    slug: data.slug || slug,
    exists,
    reserved,
    domain: data.domain || null,
    redirectUrl: data.redirect_url || null,
    message: message || null,
  };
}

export function workspaceLoginUrl(slug) {
  const base = workspacePreviewUrl(slug);
  return base ? `${base}/login` : "";
}

export function workspaceHostPreview(slug) {
  const normalized = slugifyWorkspaceName(slug);
  if (!normalized) return `${tenantDomainLabel()}`;
  return `${normalized}.${tenantDomainLabel()}`;
}

const CACHE_PREFIX = "dq_ws_exists:";

export async function verifyCurrentTenantWorkspace() {
  const slug = tenantSlugFromHost();
  if (!slug) {
    return { ok: false, reason: "invalid", slug: null };
  }

  const cacheKey = `${CACHE_PREFIX}${slug}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached === "1") return { ok: true, reason: "cached", slug };
    if (cached === "0") return { ok: false, reason: "missing", slug };
  } catch {
    /* ignore storage errors */
  }

  try {
    const result = await lookupWorkspace(slug);
    if (result.exists) {
      try {
        sessionStorage.setItem(cacheKey, "1");
      } catch {
        /* ignore */
      }
      return { ok: true, reason: "found", slug };
    }

    try {
      sessionStorage.setItem(cacheKey, "0");
    } catch {
      /* ignore */
    }

    return {
      ok: false,
      reason: result.reserved ? "reserved" : "missing",
      slug,
    };
  } catch {
    // Fail open if central lookup is unreachable so a temporary outage
    // does not lock valid workspaces out of login.
    return { ok: true, reason: "lookup_failed", slug };
  }
}
